import React, { useEffect, useMemo } from "react"

import PrestamosButtons from "#colaborador/Solicitudes/prestamos_componentes/PrestamosButtons"
import { formatMoney } from "#common/Funciones"
import { SkeletonSquare } from "#common/Skeleton"
import { PERFILES } from "#constants/Strings"
import { capitalizeSpanish } from "#core/functions/string"
import ErrorResponse from "#core/pages/ErrorResponse"
import { useApi } from "#hooks/useApi"
import { UseHasPermission } from "#hooks/useHasPermission"
import { STATUS } from "#loan/constants"
import { useRefreshLoan } from "#loan/redux/actions"
import { useLoanRedux } from "#loan/redux/slice"
import { loanGet } from "#loan/services/api"
import { Box, Tooltip } from "@mui/material"
import MUIDataTable from "mui-datatables"

export function LoanTable({ status, autoFetch = true, permiso }) {
	const { reload } = useLoanRedux()
	const { refresh } = useRefreshLoan()

	const params = useMemo(() => ({ status, all: true }), [status])
	const { data, loading, error } = useApi(loanGet, { autoFetch, params, reload })
	const loans = useMemo(() => data?.data || [], [data])

	useEffect(() => {
		setTimeout(() => {
			refresh()
		}, 300000)
	}, [refresh])

	const MoneyView = (value, tableMeta) => {
		const loan = loans[tableMeta.rowIndex]
		if (value === null) return "N/A"
		return formatMoney(value, loan.currency)
	}
	const dateView = (value) => {
		if (value === null) return "N/A"
		return value
	}

	let columns = [
		{
			name: "turno",
			label: "Turno",
			options: {
				filter: false,
				display: true,
			},
		},
		{
			name: "colaborador_nombre",
			label: "Colaborador",
			options: {
				filter: false,
				display: true,
			},
		},
		{
			name: "sucursal",
			label: "Sucursal",
			options: {
				filter: false,
				display: false,
			},
		},
		{
			name: "departamento",
			label: "Departamento",
			options: {
				filter: false,
				display: false,
			},
		},
		{
			name: "status_descripcion",
			label: "Estado",
			options: {
				filter: false,
				display: !status,
			},
		},
		{
			name: "has_savings",
			label: "Caja de ahorro",
			options: {
				filter: false,
				display: !status || [STATUS.VALIDATE_BOSS, STATUS.PROCESS].includes(status),
				customBodyRender: (value) => (value ? "Sí" : "No"),
			},
		},
		{
			name: "metodo_pago_descripcion",
			label: "Método De Pago",
			options: {
				filter: false,
				display: true,
			},
		},
		{
			name: "cantidad",
			label: "Cantidad",
			options: {
				filter: false,
				display: true,
				customBodyRender: (value, tableMeta) => {
					const loan = loans[tableMeta.rowIndex]
					if (loan.interest)
						return (
							<Tooltip
								title={`${formatMoney(loan.total_payment, loan.currency)} (${
									loan.interest || "N/A"
								}%)`}
							>
								{formatMoney(value, loan.currency)}
							</Tooltip>
						)
					return formatMoney(value, loan.currency)
				},
			},
		},

		{
			name: "interest",
			label: "Interés % ",
			options: {
				filter: false,
				display: false,
				customBodyRender: (value) => {
					if (value === null) return "N/A"
					return `${value.toLocaleString()} %`
				},
			},
		},
		{
			name: "total_payment",
			label: "Total a Pagar",
			options: {
				filter: false,
				display: false,
				customBodyRender: MoneyView,
			},
		},

		//Fechas
		{
			name: "fecha_solicitud",
			label: "Fecha Solicitud",
			options: {
				filter: false,
				display: [STATUS.PROCESS].includes(status),
			},
		},
		{
			name: "fecha_vobo_rh",
			label: "Aprobación RH",
			options: {
				filter: false,
				display: false,
				customBodyRender: dateView,
			},
		},
		{
			name: "fecha_vobo_jefe",
			label: "Aprobación Jefe",
			options: {
				filter: false,
				display: false,
				customBodyRender: dateView,
			},
		},
		{
			name: "autorizador_nombre",
			label: "Jefe Autorizado",
			options: {
				filter: false,
				display: false,
			},
		},

		{
			name: "date_deposit",
			label: "Fecha de deposito",
			options: {
				filter: false,
				display: [STATUS.FINISHED, STATUS.PAY_BACK, STATUS.PAY_BACK].includes(status),
				customBodyRender: dateView,
			},
		},
		{
			name: "day_payment",
			label: "Fecha tentativo de pago",
			options: {
				filter: false,
				display: [STATUS.FINISHED, STATUS.PAY_BACK].includes(status),
				customBodyRender: dateView,
			},
		},
		{
			name: "fecha_pago",
			label: "Fecha de pago",
			options: {
				filter: false,
				display: [STATUS.FINISHED].includes(status),
				customBodyRender: dateView,
			},
		},

		{
			name: "action", // Nombre de la columna para el botón
			label: " ", // Título de la columna
			options: {
				filter: false,
				sort: false,
				display: !!status && status !== STATUS.PAY,
				customBodyRender: (value, tableMeta) => {
					let loan = loans[tableMeta.rowIndex]

					return (
						<React.Fragment>
							<PrestamosButtons
								data={loan}
								ID={loan["id"]}
								user={permiso["user"]}
								filtro={{ status }}
							/>
						</React.Fragment>
					)
				},
			},
		},
	]

	const canSearch = UseHasPermission(PERFILES.RH, PERFILES.RH)

	const options = {
		textLabels: {
			body: {
				noMatch: "No hay solicitudes",
			},
		},
		rowsPerPage: 100,
		search: canSearch,
		filter: false,
		download: canSearch,
		print: false,
		viewColumns: false,
		selectableRows: "none",
	}

	if (loading) return <SkeletonSquare />
	if (error) return <ErrorResponse api={error} />
	columns = columns.map((col) => {
		return { ...col, label: capitalizeSpanish(col.label) }
	})
	return (
		<Box width={"100%"}>
			<MUIDataTable title={""} data={data?.data} columns={columns} options={options} />
		</Box>
	)
}
